import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  IconButton,
  ListItemIcon,
  Select,
  MenuItem,
  Stack,
  Typography,
  TextField,
  Autocomplete,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { enUS } from "@mui/x-date-pickers/locales";
import { brand, error } from "../../../../../theme/themePrimitives";
import {
  MAX_LENGTH_EMAIL,
  MAX_LENGTH_SHORT_LABEL,
} from "../../../../../utils/inputLimits";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  EditUserAttributes,
  UserAttributeResult,
} from "../../../../../services/api/types/portalConfiguration/userAttribute";
import { mapLanguageCodesToInfo } from "../../../../../context/LanguagesContext";

const commonLocaleText = {
  ...enUS,
  fieldMonthPlaceholder: (params: any) =>
    params.contentType === "letter" ? "MMM" : "MM",
};

interface AttributesFilterProps {
  attributesResult: UserAttributeResult[];
  attributes: EditUserAttributes[];
  primaryButtonColor: string | undefined;
  addRow: () => void;
  removeRow: (index: number) => void;
  handleChange: <K extends keyof EditUserAttributes>(
    index: number,
    field: K,
    value: EditUserAttributes[K],
    selectedAttr?: UserAttributeResult,
    groupHeader?: string
  ) => void;
  errors: {
    attributeKey: Record<number, string>;
    attributeValue: Record<number, string>;
  };
}

export const AttributeRow: React.FC<AttributesFilterProps> = ({
  attributesResult,
  attributes,
  primaryButtonColor,
  addRow,
  removeRow,
  handleChange,
  errors,
}) => {
  const [valueTypes, setValueTypes] = useState<{ [index: number]: string }>({});
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [isLineManagerFlags, setIsLineManagerFlags] = useState<{
    [index: number]: boolean;
  }>({});

  // "Profile language" is a synthetic bulk-edit attribute (attributeKey "language")
  // injected by the backend, scoped to the portal's currently-enabled languages —
  // unlike "role", a user has exactly one language, so it needs a single-select
  // control rather than the generic multi-select "list" rendering below.
  const languageOptions = useMemo(() => {
    const languageAttr = attributesResult.find(
      (a) => a.attributeKey === "language",
    );
    const codes = (languageAttr?.result ?? []).map((r) => ({
      languageCode: r.value,
    }));
    return mapLanguageCodesToInfo(codes);
  }, [attributesResult]);

  return (
    <>
      {attributes.map((attribute, index) => (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: "1rem",
            mb: "40px",
            alignItems: "end",
          }}
          key={index}
        >
          <Stack spacing={1} sx={{ position: "relative" }}>
            <Typography variant="subtitle2">User Attribute</Typography>
            <Select
              id={`attribute-key-select-${index}`}
              value={attribute.attributeKey}
              onChange={(e) => {
                const selectedValue = e.target.value;
                const selectedAttr = attributesResult.find(
                  (item) => item.attributeKey === selectedValue
                );
                handleChange(
                  index,
                  "attributeKey",
                  selectedValue,
                  selectedAttr
                );

                if (selectedAttr) {
                  setValueTypes((prev) => ({
                    ...prev,
                    [index]: selectedAttr.dataType,
                  }));
                  setIsLineManagerFlags((prev) => ({
                    ...prev,
                    [index]: selectedAttr.isLineManager || false,
                  }));
                } else {
                  setValueTypes((prev) => {
                    const newTypes = { ...prev };
                    delete newTypes[index];
                    return newTypes;
                  });
                  setIsLineManagerFlags((prev) => {
                    const newFlags = { ...prev };
                    delete newFlags[index];
                    return newFlags;
                  });
                }
              }}
              displayEmpty
              inputProps={{ "aria-label": "User Attribute" }}
              fullWidth
              size="small"
              IconComponent={ExpandMoreIcon}
              error={Boolean(errors.attributeKey[index])}
              renderValue={(selected) => {
                if (!selected)
                  return (
                    <Typography sx={{ color: "#667085" }}>
                      Select attribute
                    </Typography>
                  );
                const attr = attributesResult.find(
                  (a) => a.attributeKey === selected
                );
                return attr ? attr.attributeName : selected;
              }}
            >
              {attributesResult.map((attributeResult) => {
                return (
                  <MenuItem
                    key={attributeResult.attributeKey}
                    value={attributeResult.attributeKey}
                    sx={{
                      my: 0.5,
                    }}
                  >
                    {attributeResult.attributeName}
                  </MenuItem>
                );
              })}
            </Select>
            {errors.attributeKey[index] && (
              <Typography
                sx={{
                  margin: "4px 4px 0px !important",
                  position: "absolute",
                  bottom: -23,
                  left: 0,
                }}
                fontSize={"12px"}
                color={error[600]}
              >
                {errors.attributeKey[index]}
              </Typography>
            )}
          </Stack>
          {valueTypes[index] === "date" ? (
            <Stack spacing={1} sx={{ position: "relative" }}>
              <Typography variant="subtitle2">New value</Typography>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                localeText={commonLocaleText}
              >
                <DatePicker
                  format="DD-MMM-YYYY"
                  value={
                    typeof attribute.attributeValue === "string" &&
                    attribute.attributeValue
                      ? dayjs(attribute.attributeValue, "DD/MMM/YYYY")
                      : null
                  }
                  open={openIndex === index}
                  onOpen={() => setOpenIndex(index)}
                  onClose={() => setOpenIndex(null)}
                  onChange={(date) =>
                    handleChange(
                      index,
                      "attributeValue",
                      date ? dayjs(date).format("DD-MMM-YYYY") : ""
                    )
                  }
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined",
                      error: Boolean(errors.attributeValue[index]),
                      onClick: () => setOpenIndex(index),
                      InputProps: {
                        readOnly: true,
                        sx: {
                          "& .MuiInputAdornment-root button": {
                            border: "none",
                            padding: 0,
                            background: "transparent",
                            "&:hover": { background: "transparent" },
                          },
                          "& .MuiSvgIcon-root": {
                            fontSize: "1.5rem",
                          },
                        },
                        endAdornment: errors.attributeValue[index] && (
                          <ErrorOutlineIcon color="error" fontSize="small" />
                        ),
                      },
                    },
                  }}
                />
                {errors.attributeValue[index] && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{
                      position: "absolute",
                      bottom: -23,
                      left: 0,
                    }}
                  >
                    {errors.attributeValue[index]}
                  </Typography>
                )}
              </LocalizationProvider>
            </Stack>
          ) : isLineManagerFlags[index] && valueTypes[index] === "string" ? (
            <Stack spacing={1} sx={{ position: "relative" }}>
              <Typography variant="subtitle2">New value</Typography>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                open={false}
                value={
                  Array.isArray(attribute.attributeValue)
                    ? attribute.attributeValue
                    : typeof attribute.attributeValue === "string" &&
                      attribute.attributeValue
                    ? attribute.attributeValue.split("|").filter(Boolean)
                    : []
                }
                size="small"
                onChange={(_, newValue) => {
                  handleChange(index, "attributeValue", newValue);
                }}
                renderInput={(params) => {
                  const hasValues = Array.isArray(attribute.attributeValue)
                    ? attribute.attributeValue.length > 0
                    : typeof attribute.attributeValue === "string" &&
                      attribute.attributeValue
                    ? attribute.attributeValue.split("|").filter(Boolean)
                        .length > 0
                    : false;
                  return (
                    <TextField
                      {...params}
                      placeholder={hasValues ? "" : "Insert a value"}
                      error={Boolean(errors.attributeValue[index])}
                      required
                      inputProps={{
                        ...params.inputProps,
                        maxLength: MAX_LENGTH_EMAIL,
                      }}
                    />
                  );
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      sx={{
                        backgroundColor: "white",
                        padding: "14px 6px",
                        borderRadius: "6px",
                      }}
                    />
                  ))
                }
                slotProps={{
                  popupIndicator: {
                    sx: { display: "none" },
                  },
                  clearIndicator: {
                    sx: { display: "none" },
                  },
                }}
              />
              {errors.attributeValue[index] && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{
                    position: "absolute",
                    bottom: -23,
                    left: 0,
                  }}
                >
                  {errors.attributeValue[index]}
                </Typography>
              )}
            </Stack>
          ) : attribute.attributeKey === "language" ? (
            <Stack spacing={1} sx={{ position: "relative" }}>
              <Typography variant="subtitle2">New value</Typography>
              <Select
                id={`attribute-value-select-${index}`}
                value={
                  typeof attribute.attributeValue === "string"
                    ? attribute.attributeValue
                    : ""
                }
                onChange={(e) =>
                  handleChange(index, "attributeValue", e.target.value)
                }
                displayEmpty
                fullWidth
                size="small"
                IconComponent={ExpandMoreIcon}
                error={Boolean(errors.attributeValue[index])}
                renderValue={(selected) => {
                  if (!selected)
                    return (
                      <Typography sx={{ color: "#667085" }}>
                        Select a value
                      </Typography>
                    );
                  const selectedLang = languageOptions.find(
                    (lang) => lang.langugageCode === selected,
                  );
                  if (!selectedLang) return selected;
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        src={selectedLang.flag}
                        alt={selectedLang.name}
                        sx={{ width: 20, height: 20 }}
                      />
                      <Typography>{selectedLang.name}</Typography>
                    </Box>
                  );
                }}
              >
                {languageOptions.map((lang) => (
                  <MenuItem key={lang.langugageCode} value={lang.langugageCode}>
                    <ListItemIcon>
                      <Avatar
                        src={lang.flag}
                        alt={lang.name}
                        sx={{ width: 20, height: 20 }}
                      />
                    </ListItemIcon>
                    <Typography>{lang.name}</Typography>
                  </MenuItem>
                ))}
              </Select>
              {errors.attributeValue[index] && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{
                    position: "absolute",
                    bottom: -23,
                    left: 0,
                  }}
                >
                  {errors.attributeValue[index]}
                </Typography>
              )}
            </Stack>
          ) : valueTypes[index] === "list" ? (
            <Stack spacing={1} sx={{ position: "relative" }}>
              <Typography variant="subtitle2">New value</Typography>

              <Autocomplete
                id={`attribute-value-autocomplete-${index}`}
                multiple
                options={
                  attributesResult
                    .find(
                      (item) =>
                        item.attributeName === attribute.attributeKey ||
                        item.attributeKey === attribute.attributeKey
                    )
                    ?.result?.map((r) => r.value) || []
                }
                value={
                  Array.isArray(attribute.attributeValue)
                    ? attribute.attributeValue
                    : []
                }
                size="small"
                onChange={(_, newValue) => {
                  handleChange(index, "attributeValue", newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select a value"
                    error={Boolean(errors.attributeValue[index])}
                    required
                  />
                )}
                renderValue={(selected: string[], getItemProps) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {selected.map((option, index) => (
                      <Chip
                        {...getItemProps({ index })}
                        key={option}
                        label={option}
                        sx={{
                          backgroundColor: "white",
                          padding: "14px 6px",
                          borderRadius: "6px",
                        }}
                      />
                    ))}
                  </Box>
                )}
                slotProps={{
                  popupIndicator: {
                    sx: { border: "none" },
                  },
                  clearIndicator: {
                    sx: { display: "none" },
                  },
                }}
              />

              {errors.attributeValue[index] && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{
                    position: "absolute",
                    bottom: -23,
                    left: 0,
                  }}
                >
                  {errors.attributeValue[index]}
                </Typography>
              )}
            </Stack>
          ) : (
            <Stack spacing={1} sx={{ position: "relative" }}>
              <Typography variant="subtitle2">New value</Typography>
              <TextField
                fullWidth
                id={`newValue-${index}`}
                placeholder="Insert a value"
                type={valueTypes[index] || "text"}
                value={attribute.attributeValue}
                onChange={(e) =>
                  handleChange(index, "attributeValue", e.target.value)
                }
                variant="outlined"
                size="small"
                error={Boolean(errors.attributeValue[index])}
                slotProps={{
                  input: {
                    endAdornment: errors.attributeValue[index] && (
                      <ErrorOutlineIcon color="error" fontSize="small" />
                    ),
                  },
                  htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
                }}
              />
              {errors.attributeValue[index] && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{
                    position: "absolute",
                    bottom: -23,
                    left: 0,
                  }}
                >
                  {errors.attributeValue[index]}
                </Typography>
              )}
            </Stack>
          )}
          {index === 0 && (
            <IconButton
              sx={{
                backgroundColor: `${brand[600]} !important`,
                "&:hover": {
                  bgcolor: `${brand[700]} !important`,
                },
              }}
              onClick={() => {
                addRow();
              }}
            >
              <AddIcon sx={{ color: "white" }} />
            </IconButton>
          )}
          {index > 0 && (
            <IconButton
              sx={{
                backgroundColor: `${error[50]} !important`,
                borderColor: `${error[600]} !important`,
              }}
              onClick={() => removeRow(index)}
            >
              <RemoveIcon sx={{ color: error[600] }} />
            </IconButton>
          )}
        </Box>
      ))}
    </>
  );
};
